import { Events, Browser } from '@clappr/core';
import { MediaControl } from '@clappr/plugins';

function formatTimeSpoken(seconds) {
  if (!seconds || !isFinite(seconds)) return '0 seconds';
  seconds = Math.floor(seconds);
  var hours = Math.floor(seconds / 3600);
  var minutes = Math.floor((seconds % 3600) / 60);
  var secs = seconds % 60;
  var parts = [];
  if (hours > 0) parts.push(hours + (hours === 1 ? ' hour' : ' hours'));
  if (minutes > 0) parts.push(minutes + (minutes === 1 ? ' minute' : ' minutes'));
  if (secs > 0 || parts.length === 0) parts.push(secs + (secs === 1 ? ' second' : ' seconds'));
  return parts.join(' and ');
}

export default MediaControl.extend({
  render() {
    const res = MediaControl.prototype.render.call(this);
    this._reorderRightPanel();
    this._addTabToVolume();
    this._addTabToSeekBar();
    this._updatePlayPauseAria();
    this._addTabToNativeFullscreen();
    this._setupFocusShowControls();
    this.onVolumeChanged(this.volume);
    return res;
  },

  _reorderRightPanel() {
    const panel = this.$el.find('.media-control-right-panel')[0];
    if (!panel || this._reordering) return;
    this._reordering = true;
    try {
      const nodes = Array.from(panel.children);
      for (let i = nodes.length - 1; i >= 0; i--) {
        panel.appendChild(nodes[i]);
      }
    } finally {
      this._reordering = false;
    }
  },

  _addTabToVolume() {
    const volumeButton = this.$el.find('.media-control-icon[data-volume]');
    const volumeBarContainer = this.$el.find('.bar-container[data-volume]');

    if (volumeButton.length > 0) {
      volumeButton.attr('role', 'button');
      volumeButton.attr('tabindex', 0);
      volumeButton.attr('aria-label', 'Mute');
      volumeButton.on('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.toggleMute();
        }
      });
    }

    if (volumeBarContainer.length > 0) {
      volumeBarContainer.attr('tabindex', '0');
      volumeBarContainer.attr('role', 'slider');
      volumeBarContainer.attr('aria-label', 'Volume');
      volumeBarContainer.attr('aria-valuemin', '0');
      volumeBarContainer.attr('aria-valuemax', '100');
      volumeBarContainer.attr('aria-valuenow', String(this.volume || 100));
      volumeBarContainer.on('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.setVolume(Math.min(100, (this.volume || 0) + 10));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.setVolume(Math.max(0, (this.volume || 0) - 10));
        }
      });
    }
  },

  _updateVolumeAria() {
    var volumeBarContainer = this.$el && this.$el.find('.bar-container[data-volume]');
    if (!volumeBarContainer || !volumeBarContainer.length) return;
    var vol = Math.round((this.volume || 0) / 10) * 10;
    volumeBarContainer.attr('aria-valuenow', String(vol));
    volumeBarContainer.attr('aria-valuetext', vol + ' percent volume');
  },

  _addTabToSeekBar() {
    const seekBar = this.$el.find('.bar-container[data-seekbar]');
    if (seekBar.length > 0) {
      seekBar.attr('tabindex', '0');
      seekBar.attr('role', 'slider');
      seekBar.attr('aria-label', 'Seek');
      seekBar.attr('aria-valuemin', '0');
      seekBar.attr('aria-valuemax', '100');
      seekBar.on('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.seekRelative(10);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.seekRelative(-10);
        }
      });
    }
  },

  _setupFocusShowControls() {
    const el = this.$el && this.$el[0];
    if (!el || this._focusListenerAdded) return;
    this._focusListenerAdded = true;

    // When a control receives keyboard focus (e.g. Tab into the seekbar),
    // make the controls visible. We do the minimal DOM work first, then
    // fire MEDIACONTROL_SHOW only once per show cycle (not on every focus
    // change between controls). The event is needed so plugins like the
    // settings cog reset their forceMenuClose state, but firing it on
    // every focusin would cause repeated re-renders that disrupt navigation.
    el.addEventListener('focusin', () => {
      if (this.disabled) return;
      var wasHidden = this.$el.hasClass('media-control-hide');
      clearTimeout(this.hideId);
      this.$el.show();
      this.$el.removeClass('media-control-hide');
      this.updateCursorStyle(true);
      if (wasHidden) {
        this.trigger(Events.MEDIACONTROL_SHOW, this.name);
        this.container && this.container.trigger(Events.CONTAINER_MEDIACONTROL_SHOW, this.name);
      }
    });

    // When focus leaves the media control entirely, start the hide timer.
    // We check relatedTarget so that tabbing between controls (e.g. seekbar
    // to play/pause) doesn't trigger a hide.
    el.addEventListener('focusout', (e) => {
      if (!el.contains(e.relatedTarget)) {
        if (this._isPlaying()) {
          this.hideId = setTimeout(() => this.hide(), 2000);
        }
      }
    });
  },


  _updatePlayPauseAria() {
    const btn = this.$el && this.$el.find('button[data-playpause]');
    if (!btn || !btn.length) return;
    const playing = this._isPlaying();
    btn.attr('aria-label', playing ? 'Pause' : 'Play');
    btn.attr('tabindex', '0');
  },

  _addTabToNativeFullscreen() {
    const btn = this.$el && this.$el.find('button[data-fullscreen]');
    if (!btn || !btn.length) return;
    btn.attr('tabindex', '0');
  },

  _updateSeekBarAria() {
    if (!this.isVisible()) return;
    var seekBar = this.$el && this.$el.find('.bar-container[data-seekbar]');
    if (!seekBar || !seekBar.length) return;
    // Don't update while the seekbar is focused — VoiceOver announces every
    // aria-valuetext change, which talks over the video. The user hears the
    // position when they Tab to it; it stays quiet while they listen.
    if (seekBar[0] === document.activeElement) return;
    var duration = this.container && this.container.getDuration ? this.container.getDuration() : 0;
    var current = this.container && this.container.getCurrentTime ? this.container.getCurrentTime() : 0;
    var pct = duration ? Math.round((current / duration) * 100) : 0;
    seekBar.attr('aria-valuenow', String(pct));
    seekBar.attr('aria-valuetext', formatTimeSpoken(current) + ' of ' + formatTimeSpoken(duration));
  },

  _isPlaying() {
    return !!(this.container && this.container.isPlaying && this.container.isPlaying());
  },

  _wrappedResetUserKeepVisible() {
    if (!this._isPlaying()) {
        return;
    }
    this.resetUserKeepVisible();
  },

  changeTogglePlay() {
    MediaControl.prototype.changeTogglePlay.call(this);
    this._updatePlayPauseAria();
    this.$el.toggleClass('media-control-paused', !this._isPlaying());
  },

  bindContainerEvents() {
    if (!this.container) return;
    this.listenTo(this.container, Events.CONTAINER_PLAY, this.changeTogglePlay);
    this.listenTo(this.container, Events.CONTAINER_PAUSE, this.changeTogglePlay);
    this.listenTo(this.container, Events.CONTAINER_STOP, this.changeTogglePlay);
    this.listenTo(this.container, Events.CONTAINER_TIMEUPDATE, this.onTimeUpdate);
    this.listenTo(this.container, Events.CONTAINER_PROGRESS, this.updateProgressBar);
    this.listenTo(this.container, Events.CONTAINER_SETTINGSUPDATE, this.settingsUpdate);
    this.listenTo(this.container, Events.CONTAINER_PLAYBACKDVRSTATECHANGED, this.settingsUpdate);
    this.listenTo(this.container, Events.CONTAINER_HIGHDEFINITIONUPDATE, this.highDefinitionUpdate);
    this.listenTo(this.container, Events.CONTAINER_MEDIACONTROL_DISABLE, this.disable);
    this.listenTo(this.container, Events.CONTAINER_MEDIACONTROL_ENABLE, this.enable);
    this.listenTo(this.container, Events.CONTAINER_ENDED, this.ended);
    this.listenTo(this.container, Events.CONTAINER_VOLUME, this.onVolumeChanged);
    this.listenTo(this.container, Events.CONTAINER_OPTIONS_CHANGE, this.setInitialVolume);
    if (this.allowNativeFullscreen) {
      this.listenTo(this.container, Events.CONTAINER_DBLCLICK, this.toggleFullscreen);
    }

    // Update seekbar aria when controls are visible
    this.listenTo(this.container, Events.CONTAINER_TIMEUPDATE, () => this._updateSeekBarAria());

    // Update volume slider aria when volume changes
    this.listenTo(this.container, Events.CONTAINER_VOLUME, () => this._updateVolumeAria());

    // Custom Boxcast logic
    var video = this.container.playback.el;
    if (video.nodeName.toLowerCase() === 'video') {
      // wait until the metadata has loaded and then check if fullscreen on video tag is supported
      this.listenToOnce(
        this.container,
        Events.CONTAINER_LOADEDMETADATA,
        this.onLoadedMetadataOnVideoTag
      );

      // on touch devices, always show controls if they tap through to video
      video.addEventListener('touchstart', () => this.show());

      // watch for user to come out of fullscreen and remove the native controls
      // that iOS tends to add in for some reason.
      video.addEventListener(
        'webkitendfullscreen',
        function () {
          video.removeAttribute('controls');
        },
        false
      );
    }
  },

  get allowNativeFullscreen() {
    // If using the fullscreen placeholder plugin, do not allow native fullscreen functionality
    if (this.options.fullscreenPlaceholder) {
      return false;
    }
    return true;
  },

  get events() {
    var e = {
      // play/pause/stop
      'click [data-play]': 'play',
      'click [data-pause]': 'pause',
      'click [data-playpause]': 'togglePlayPause',
      'click [data-stop]': 'stop',
      'click [data-playstop]': 'togglePlayStop',

      // volume
      'click .bar-container[data-volume]': 'onVolumeClick',
      'click .drawer-icon[data-volume]': 'toggleMute',
      'mouseenter .drawer-container[data-volume]': 'showVolumeBar',
      'mouseleave .drawer-container[data-volume]': 'hideVolumeBar',
      'mousedown .bar-container[data-volume]': 'startVolumeDrag',
      'mousemove .bar-container[data-volume]': 'mousemoveOnVolumeBar',

      // seek
      'click .bar-container[data-seekbar]': 'clickOrTapToSeek',
      'mousedown .bar-scrubber[data-seekbar]': 'startSeekDrag',
      'mousemove .bar-container[data-seekbar]': 'mousemoveOnSeekBar',
      'mouseleave .bar-container[data-seekbar]': 'mouseleaveOnSeekBar',

      // show/hide when mouse over or keyboard focus
      'mouseenter .media-control-layer[data-controls]': 'setUserKeepVisible',
      'mouseleave .media-control-layer[data-controls]': '_wrappedResetUserKeepVisible',
    };

    // Seek with touch events is needed on iOS, but not Android, which tends to emulate them
    if (Browser.isiOS) {
      e['touchstart .bar-scrubber[data-seekbar]'] = 'startSeekDrag';
      e['touchmove .bar-container[data-seekbar]'] = 'touchMoveOnSeekBar';
      e['touchend .bar-container[data-seekbar]'] = 'touchEndOnSeekbar';
    }

    // If using the fullscreen placeholder plugin, do not allow native fullscreen functionality
    if (this.allowNativeFullscreen) {
      e['click [data-fullscreen]'] = 'toggleFullscreen';
    }

    return e;
  },

  onVolumeChanged(volume) {

    MediaControl.prototype.onVolumeChanged.call(this, volume);

    const volumeIcon = this.$el.find('.drawer-icon[data-volume]');
    const isMuted = volume === 0;
    
    volumeIcon.toggleClass('is-muted', isMuted);
    volumeIcon.attr('aria-label', isMuted ? 'Unmute' : 'Mute');
  },

  clickOrTapToSeek: function (event) {
    this.seek(event);

    // Make sure to also hide the hover indicator if it were there on touch devices
    this.mouseleaveOnSeekBar(event);
  },

  touchMoveOnSeekBar: function (event) {
    this.mousemoveOnSeekBar(event);

    // If we hid the seek dot on touchEnd from last time, make sure to restore it
    // so you can see where you're dragging
    this.$seekBarHover.css({ opacity: 1 });
  },

  touchEndOnSeekbar: function (event) {
    // Touch event is a little different than mouse hover, in that we actually
    // want to perform a seek when they're done dragging.  For mouse, we let
    // them hover to see the scrub thumbnail but require a separate click to seek.
    this.mouseleaveOnSeekBar(event);

    // Pretend this was part of the grab/drag functionality with mouse-based interaction,
    // which will perform a seek when done dragging.
    this.draggingSeekBar = true;
    this.stopDrag(event);
    event.preventDefault();

    // Also, that silly `bar-hover` dot is useless for touch, and gets stuck being displayed
    // when iOS decides to keep the :hover selector on. Just get rid of it.
    this.$seekBarHover.css({ opacity: 0 });
  },

  show: function (event) {
    if (this.disabled) return;

    const timeout = 2000;
    let mousePointerMoved =
      event && event.clientX !== this.lastMouseX && event.clientY !== this.lastMouseY;
    if (!event || mousePointerMoved || navigator.userAgent.match(/firefox/i)) {
      clearTimeout(this.hideId);
      this.$el.show();
      this.trigger(Events.MEDIACONTROL_SHOW, this.name);
      this.container && this.container.trigger(Events.CONTAINER_MEDIACONTROL_SHOW, this.name);
      this.$el.removeClass('media-control-hide');
      this.hideId = setTimeout(() => this.hide(), timeout);
      if (event) {
        this.lastMouseX = event.clientX;
        this.lastMouseY = event.clientY;
      }
    }
    const showing = true;
    this.updateCursorStyle(showing);

    if (!this.allowNativeFullscreen) {
      this.$el.find('[data-fullscreen]:not(.placeholder)').hide();
    }
  },

  hide: function () {
    if (!this._isPlaying()) {
      return;
    }

    const res = MediaControl.prototype.hide.call(this);
    return res;
  },
});
